import NumberFlow from '@number-flow/react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';
import { useTheme } from 'styled-components';

import { Accordion, Card, DataCard, Heading3, Paragraph } from '@treams/treams-ui';

import { ScoreCategory, type ScoreFactor, ScoreModuleState } from '../TreamsScore.types';
import { useScoreColors } from '../hooks/useTreamsScore';
import { toDisplayScore } from '../scoreDisplay';
import {
	DashPlaceholder,
	ExplanationList,
	FactorLabel,
	FactorSection,
	Intro,
	LevelTable,
	Row,
	ScrollArea,
	Weight,
} from './TreamsScoreExplanation.styles';

type Props = {
	factors: ScoreFactor[];
};

const EXPLANATION_PREFIX = 'analytics.treamsScore.explanation';

// Exhaustive on ScoreCategory: adding a category without explanation keys is a compile error.
const EXPLANATION_KEYS: Record<ScoreCategory, string[]> = {
	[ScoreCategory.Engagement]: [
		`${EXPLANATION_PREFIX}.factor.engagement.item1`,
		`${EXPLANATION_PREFIX}.factor.engagement.item2`,
		`${EXPLANATION_PREFIX}.factor.engagement.item3`,
		`${EXPLANATION_PREFIX}.factor.engagement.item4`,
	],
	[ScoreCategory.OneOnOne]: [
		`${EXPLANATION_PREFIX}.factor.oneOnOne.item1`,
		`${EXPLANATION_PREFIX}.factor.oneOnOne.item2`,
	],
	[ScoreCategory.Feedback]: [
		`${EXPLANATION_PREFIX}.factor.feedback.item1`,
		`${EXPLANATION_PREFIX}.factor.feedback.item2`,
		`${EXPLANATION_PREFIX}.factor.feedback.item3`,
		`${EXPLANATION_PREFIX}.factor.feedback.item4`,
	],
	[ScoreCategory.Targets]: [
		`${EXPLANATION_PREFIX}.factor.targets.item1`,
		`${EXPLANATION_PREFIX}.factor.targets.item2`,
		`${EXPLANATION_PREFIX}.factor.targets.item3`,
		`${EXPLANATION_PREFIX}.factor.targets.item4`,
	],
	[ScoreCategory.Conversations]: [
		`${EXPLANATION_PREFIX}.factor.conversations.item1`,
		`${EXPLANATION_PREFIX}.factor.conversations.item2`,
	],
};

const SCORE_LEVELS = [
	{ labelKey: `${EXPLANATION_PREFIX}.level.forming`, min: 1, max: 5 },
	{ labelKey: `${EXPLANATION_PREFIX}.level.building`, min: 5, max: 6.5 },
	{ labelKey: `${EXPLANATION_PREFIX}.level.developing`, min: 6.5, max: 7.5 },
	{ labelKey: `${EXPLANATION_PREFIX}.level.performing`, min: 7.5, max: 8.5 },
	{ labelKey: `${EXPLANATION_PREFIX}.level.highPerforming`, min: 8.5, max: 10 },
];

const TreamsScoreExplanation = ({ factors }: Props) => {
	const { t, i18n } = useTranslation();
	const theme = useTheme();
	const scoreColors = useScoreColors();

	const formatLevelScore = (value: number) =>
		value.toLocaleString(i18n.language, { minimumFractionDigits: 1, maximumFractionDigits: 1 });

	const faqItems = [
		{
			question: t(`${EXPLANATION_PREFIX}.faq.function.question`),
			answer: (
				<>
					<Paragraph mb={2}>{t(`${EXPLANATION_PREFIX}.faq.function.answer1`)}</Paragraph>
					<Paragraph>{t(`${EXPLANATION_PREFIX}.faq.function.answer2`)}</Paragraph>
				</>
			),
		},
		{
			question: t(`${EXPLANATION_PREFIX}.faq.levels.question`),
			answer: (
				<LevelTable>
					<thead>
						<tr>
							<th>{t(`${EXPLANATION_PREFIX}.faq.levels.columnLevel`)}</th>
							<th>{t(`${EXPLANATION_PREFIX}.faq.levels.columnScore`)}</th>
						</tr>
					</thead>
					<tbody>
						{SCORE_LEVELS.map(({ labelKey, min, max }) => (
							<tr key={labelKey}>
								<td>{t(labelKey)}</td>
								<td>
									{formatLevelScore(min)} &ndash; {formatLevelScore(max)}
								</td>
							</tr>
						))}
					</tbody>
				</LevelTable>
			),
		},
		{
			question: t(`${EXPLANATION_PREFIX}.faq.moduleDisabled.question`),
			answer: <Paragraph>{t(`${EXPLANATION_PREFIX}.faq.moduleDisabled.answer`)}</Paragraph>,
		},
		{
			question: t(`${EXPLANATION_PREFIX}.faq.weighting.question`),
			answer: <Paragraph>{t(`${EXPLANATION_PREFIX}.faq.weighting.answer`)}</Paragraph>,
		},
		{
			question: t(`${EXPLANATION_PREFIX}.faq.visibility.question`),
			answer: <Paragraph>{t(`${EXPLANATION_PREFIX}.faq.visibility.answer`)}</Paragraph>,
		},
	];

	return (
		<ScrollArea p={3}>
			<Heading3 pb={3}>{t(`${EXPLANATION_PREFIX}.title`)}</Heading3>
			<Intro mb={3}>{t(`${EXPLANATION_PREFIX}.intro1`)}</Intro>
			<Intro mb={3}>{t(`${EXPLANATION_PREFIX}.intro2`)}</Intro>

			{factors.length > 0 && (
				<Card px={0} py={0}>
					{factors.map((factor) => {
						const isUnavailable = factor.state === ScoreModuleState.Unavailable;
						const isDisabled = factor.state === ScoreModuleState.Disabled;
						const isInactive = isUnavailable || isDisabled;
						const displayScore = toDisplayScore(isDisabled ? 0 : factor.score);

						return (
							<FactorSection key={factor.category}>
								<Row flexWrap="wrap">
									<Box
										flexGrow={1}
										display="flex"
										alignItems="center"
										justifyContent="space-between"
									>
										<DataCard
											background={
												isInactive
													? theme.colors.lightBlueLight
													: scoreColors(displayScore).background
											}
										>
											{isUnavailable ? (
												<DashPlaceholder />
											) : (
												<NumberFlow
													value={displayScore}
													format={{ maximumFractionDigits: 1 }}
													locales={i18n.language}
												/>
											)}
										</DataCard>

										<FactorLabel pl={2} $inactive={isInactive}>
											{factor.label}
										</FactorLabel>
									</Box>

									<Weight>
										<NumberFlow value={factor.weight} suffix="%" locales={i18n.language} />{' '}
										{t(`${EXPLANATION_PREFIX}.weight`)}
									</Weight>
								</Row>
								<ExplanationList>
									{EXPLANATION_KEYS[factor.category].map((itemKey) => (
										<li key={itemKey}>{t(itemKey)}</li>
									))}
								</ExplanationList>
							</FactorSection>
						);
					})}
				</Card>
			)}

			<Heading3 pt={3} pb={2}>
				{t(`${EXPLANATION_PREFIX}.faq.title`)}
			</Heading3>
			{faqItems.map(({ question, answer }) => (
				<Accordion key={question} title={question}>
					{answer}
				</Accordion>
			))}
		</ScrollArea>
	);
};

export default TreamsScoreExplanation;
